import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { EnumHearAboutSurvey, EnumSections, MAppMeta, MProfile } from 'src/private/redux/scholarship.models';

import { applicationSetField } from '../../../redux/scholarship.actions';
import { selectProfilesChild, selectProfilesParent } from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

interface IApplicationsFormMetaSection extends MAppMeta {
  hearAboutSurvey?: string;
  hearAboutSurveyOther?: string;
}

interface IApplicationsFormMetaProps {
  id: string;
  thisSection: IApplicationsFormMetaSection;
  dispatchApplicationUpdate: (id: string, section: EnumSections, field: string, value: unknown) => void;
}

const ApplicationsFormMeta = ({ id, thisSection, dispatchApplicationUpdate }: IApplicationsFormMetaProps) => {
  const [profileCreated, setProfileCreated] = useState<boolean>(false);
  const { trackPageView } = useMatomo();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    if (thisSection.id !== 'new' && thisSection.linkedChildProfile && thisSection.linkedParentProfile) {
      setProfileCreated(true);
    }
  }, []);

  const profilesParent = useSelector<unknown, MProfile[]>(selectProfilesParent as (state: unknown) => MProfile[]);
  const profilesChild = useSelector<unknown, MProfile[]>(selectProfilesChild as (state: unknown) => MProfile[]);

  useEffect(() => {
    // check if we have a currently selected child profile
    if (thisSection.linkedChildProfile) {
      // find the profile this is referring to
      const selectedProfile = profilesChild.find((profile) => profile.id === thisSection.linkedChildProfile);

      // if we found the correct profile
      if (selectedProfile) {
        // update the metadata to reflect this profile's lastUpdated property
        if (thisSection.lastUpdated !== selectedProfile.lastUpdated) {
          thisSection.lastUpdated = selectedProfile.lastUpdated;
        }
      }
    }
  }, [profilesChild]);

  const hearAboutSelect = [
    {
      label: 'Email',
      value: EnumHearAboutSurvey.EMAIL
    },
    {
      label: 'Event',
      value: EnumHearAboutSurvey.EVENT
    },
    {
      label: 'Facebook',
      value: EnumHearAboutSurvey.FACEBOOK
    },
    {
      label: 'Family, Friend or Colleague',
      value: EnumHearAboutSurvey.PERSON
    },
    {
      label: 'News Article',
      value: EnumHearAboutSurvey.ARTICLE
    },
    {
      label: 'TV/Radio Interview',
      value: EnumHearAboutSurvey.INTERVIEW
    },
    {
      label: 'Twitter',
      value: EnumHearAboutSurvey.TWITTER
    },
    {
      label: 'Website/Internet',
      value: EnumHearAboutSurvey.WEB
    },
    {
      label: 'Other',
      value: EnumHearAboutSurvey.OTHER
    }
  ];

  return (
    <ApplicationFormContainer>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Profiles</Typography>
        </Grid>
        {profileCreated && (
          <Grid item xs={12}>
            <Alert severity="info">
              You can edit the student and parent profiles associated with this application; however, you cannot select a new student
              profile. Click "Save and Continue" to finish filling out this application, or "Back" to return to the applications page.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box bgcolor="#f3f6fd" borderRadius={4} p={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }} gutterBottom>
              Student Profile(s)
            </Typography>
            <Typography variant="body2" gutterBottom>
              Create a profile for the child on whose behalf you are submitting this application or select and edit an existing profile.
              Please note that you can establish profiles here for more than one of your children if they meet the eligibility requirements
              of the Match the Promise program.
            </Typography>
            <Grid container justifyContent="space-between" direction="column" style={{ marginTop: '18px' }}>
              <Grid item>
                <RadioGroup
                  aria-label="child-profile"
                  name="linkedChildProfile"
                  value={thisSection?.linkedChildProfile || ''}
                  onChange={(event) => {
                    dispatchApplicationUpdate(id, EnumSections.META, 'linkedChildProfile', event.target.value);

                    const profile = profilesChild.filter((row: MProfile) => row.id === event.target.value);
                    dispatchApplicationUpdate(id, EnumSections.META, 'lastUpdated', profile[0].lastUpdated);
                  }}
                >
                  {profilesChild
                    .filter((row: MProfile) => !!row.firstName)
                    .map((row: MProfile) => (
                      <Grid container style={{ marginBottom: 5 }} alignItems="center" key={'child_' + row.id}>
                        <Grid item xs="auto">
                          <FormControlLabel
                            key={row.id}
                            value={row.id}
                            control={<Radio />}
                            label={`${row.firstName} ${row.lastName}`}
                            disabled={profileCreated}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <Link to={`/applications/profiles/child/${row.id}`}>Review/Edit</Link>
                        </Grid>
                        {(!row.lastUpdated || row.lastUpdated.toString() !== DateTime.now().setZone('America/Chicago').year.toString()) && (
                          <Grid item style={{ marginLeft: 10 }} xs="auto" className="ps-1">
                            <Alert severity="warning">Needs Review: Grade may be out of date.</Alert>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                </RadioGroup>
              </Grid>
              <Grid item>
                <Grid container alignItems="flex-end" direction="column">
                  <Grid item>
                    <Button component={Link} size="small" variant="contained" color="primary" to={'/applications/profiles/child/new'}>
                      Create a new student profile
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box bgcolor="#f3f6fd" borderRadius={4} p={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }} gutterBottom>
              Parent Profile
            </Typography>
            <Typography variant="body2" gutterBottom>
              Create a profile for yourself or select and edit an existing profile. Please note that you can submit this application on
              behalf of your child if you claimed the child as a dependent on your latest federal income tax return.
            </Typography>
            <Grid container justifyContent="space-between" style={{ marginTop: '18px' }}>
              <Grid item>
                <RadioGroup
                  aria-label="parent-profile"
                  name="linkedParentProfile"
                  value={thisSection?.linkedParentProfile || ''}
                  onChange={(event) => dispatchApplicationUpdate(id, EnumSections.META, 'linkedParentProfile', event.target.value)}
                >
                  {profilesParent
                    .filter((row: MProfile) => !!row.firstName)
                    .map((row: MProfile) => (
                      <div key={'parent_' + row.id}>
                        <FormControlLabel
                          key={row.id}
                          value={row.id}
                          control={<Radio />}
                          label={`${row.firstName} ${row.lastName}`}
                          disabled={profileCreated}
                        />
                        <Link to={`/applications/profiles/parent/${row.id}`}>edit</Link>
                      </div>
                    ))}
                </RadioGroup>
              </Grid>
              <Grid item>
                {profilesParent.length <= 0 && (
                  <Button component={Link} size="small" variant="contained" color="primary" to={'/applications/profiles/parent/new'}>
                    Create a new parent/guardian profile
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">How did you learn about the Match the Promise program?</Typography>
          <FormControl variant="outlined" style={{ minWidth: 160, backgroundColor: '#e9e9e9', marginTop: 5 }}>
            <TextField
              select
              required
              value={thisSection?.hearAboutSurvey || ''}
              onChange={(event) => {
                dispatchApplicationUpdate(id, EnumSections.META, 'hearAboutSurvey', event.target.value);
                if (event.target.value !== 'other') {
                  dispatchApplicationUpdate(id, EnumSections.META, 'hearAboutSurveyOther', '');
                }
              }}
            >
              {hearAboutSelect.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {thisSection.hearAboutSurvey === EnumHearAboutSurvey.OTHER && (
            <TextField
              label="Please specify"
              value={thisSection?.hearAboutSurveyOther || ''}
              fullWidth
              onChange={(event) => dispatchApplicationUpdate(id, EnumSections.META, 'hearAboutSurveyOther', event.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">
            Before continuing through the application process, make sure that a student profile and your parent profile are selected above.
          </Typography>
        </Grid>
      </Grid>
    </ApplicationFormContainer>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchApplicationUpdate: (id: string, section: EnumSections, field: string, value: unknown): void => {
    dispatch(applicationSetField(id, section, field, value));
  }
});
export default connect(null, mapDispatchToProps)(ApplicationsFormMeta);
